import { useParentPosition } from "@/hooks/useParentPosition";
import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";
import { useTranslations } from "next-intl";
import { useRef, useState, useEffect } from "react";
import { Guest } from "@/types/Guest";
import { useAppointmentGuest } from "@/providers/AppointmentGuestProvider";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

type GuestSearchProps = {
  inputRef?: React.RefObject<HTMLInputElement | null>;
  border?: string;
  isFilter?: boolean;
  defaultValue?: string | Promise<string>;
  required?: boolean;
  setSelectedGuest?: (guest: Guest | null) => void;
  onFocusAction?: () => void;
  onBlurAction?: (guest: Guest | null) => void;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  editMode?: boolean;
  label?: string;
  rounded?: string;
  hoverMessage?: string;
  width?: string;
  labelMargin?: string;
  placeholder?: string;
  maxResults?: number;
  labelFontSize?: string;
  disabled?: boolean;
  excludeGuestId?: string;
  /** Restrict search results to guests with guestType === mainGuest. */
  mainGuestOnly?: boolean;
};

export const GuestSearch: React.FC<GuestSearchProps> = ({
  inputRef: externalInputRef,
  border = "outline-primary focus:border focus:outline-focus-bam border focus:outline-hidden",
  defaultValue = "",
  isFilter = false,
  required = false,
  setSelectedGuest,
  onFocusAction,
  onBlurAction,
  height = "h-9",
  surface = "surface-primary",
  labelFontSize = "body-2-regular",
  fontSize = "body-2-regular",
  editMode = false,
  padding = "px-2 py-1",
  label,
  rounded = "rounded-[10px]",
  hoverMessage,
  width = "w-full",
  labelMargin = "mb-2",
  placeholder,
  maxResults = 10,
  disabled,
  excludeGuestId,
  mainGuestOnly = false,
}) => {
  const t = useTranslations("inputColumnComponent");
  const { searchGuests, getRelevantGuestName, setGuestState } =
    useAppointmentGuest();

  // State
  const [inputValue, setInputValue] = useState<string>("");
  const [isLoadingDefaultValue, setIsLoadingDefaultValue] = useState(false);
  const [selectedGuest, setSelectedGuestState] = useState<Guest | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<Guest[]>([]);
  const [recommendationsWithNames, setRecommendationsWithNames] = useState<
    Array<{ guest: Guest; displayName: string }>
  >([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [maxHeight, setMaxHeight] = useState(240);
  const [positionReady, setPositionReady] = useState(false);

  // Refs
  const isMouseDownOnDropdown = useRef(false);
  const isGuestSelected = useRef(false);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef || internalInputRef;

  // Hooks
  // Label hint: hover-a-bit-longer, portalled bubble. No scroll re-tracking, so
  // it stays put (and keeps its size) while the pointer crosses a form column.
  const {
    ref: labelRef,
    onMouseEnter: onLabelMouseEnter,
    onMouseLeave: onLabelMouseLeave,
    tooltip: labelTooltip,
  } = useDelayedTooltip<HTMLLabelElement>(hoverMessage, 500, disabled);

  const { dropdownPosition, setDropdownPosition } = useParentPosition();

  // =========================================================
  // ROBUST AUTO-TRACKING IMPLEMENTATION
  // =========================================================

  // 1. Live Tracking Effect (Scroll & Resize)
  useEffect(() => {
    if (showRecommendations && inputRef.current) {
      const updatePosition = () => {
        // Double check ref existence inside the listener
        if (inputRef.current) {
          setDropdownPosition(inputRef);
        }
      };

      // Capture phase (true) is crucial for detecting scroll in nested containers
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showRecommendations, inputRef, setDropdownPosition]);

  // 2. Immediate Failsafe Effect
  // Ensures position is calculated immediately when state triggers, preventing (0,0) flicker
  useEffect(() => {
    if (showRecommendations && inputRef.current) {
      setDropdownPosition(inputRef);
    }
  }, [showRecommendations, inputRef, setDropdownPosition]);

  // 3. Wait for position to be calculated before marking ready
  useEffect(() => {
    if (showRecommendations && dropdownPosition.top !== 0) {
      setPositionReady(true);
    }
  }, [showRecommendations, dropdownPosition]);

  // =========================================================

  // Helper function to get display name
  const getGuestDisplayName = async (guest: Guest): Promise<string> => {
    const name = [guest.firstName, guest.lastName]
      .filter(Boolean)
      .join(" ");
    return (
      name ||
      guest.email ||
      (await getRelevantGuestName(guest.guestId || "", true)) ||
      ""
    );
  };

  // Update input value when defaultValue changes
  useEffect(() => {
    const loadDefaultValue = async () => {
      if (defaultValue instanceof Promise) {
        setIsLoadingDefaultValue(true);
        try {
          const resolvedValue = await defaultValue;
          setInputValue(resolvedValue);
        } catch {
          setInputValue("");
        } finally {
          setIsLoadingDefaultValue(false);
        }
      } else {
        setInputValue(defaultValue);
      }
    };

    loadDefaultValue();
  }, [defaultValue]);

  // Calculate dropdown max height based on available space
  useEffect(() => {
    if (showRecommendations) {
      const windowHeight = window.innerHeight;

      if (dropdownPosition.showAbove) {
        const availableSpace = dropdownPosition.top - 16;
        const calculatedMaxHeight = Math.min(240, availableSpace);
        setMaxHeight(calculatedMaxHeight > 0 ? calculatedMaxHeight : 240);
      } else {
        const dropdownTop = dropdownPosition.top;
        const availableSpace = windowHeight - dropdownTop - 16;
        const calculatedMaxHeight = Math.min(240, availableSpace);
        setMaxHeight(calculatedMaxHeight > 0 ? calculatedMaxHeight : 240);
      }
    }
  }, [showRecommendations, dropdownPosition]);

  // Resolve display names for recommendations
  useEffect(() => {
    const resolveNames = async () => {
      const withNames = await Promise.all(
        recommendations.map(async (guest) => ({
          guest,
          displayName: await getGuestDisplayName(guest),
        })),
      );
      setRecommendationsWithNames(withNames);
    };

    if (recommendations.length > 0) {
      resolveNames();
    } else {
      setRecommendationsWithNames([]);
    }
  }, [recommendations]);

  const updateRecommendations = async (value: string) => {
    setLoading(true);
    const results = await searchGuests(value, { mainGuestOnly });

    // Deduplicate by guestId
    const seen = new Set<string>();
    const uniqueResults = results.filter((guest) => {
      if (guest.guestId && seen.has(guest.guestId)) {
        return false;
      }
      if (guest.guestId) {
        seen.add(guest.guestId);
      }
      return true;
    });

    const filteredResults = excludeGuestId
      ? uniqueResults.filter((guest) => guest.guestId !== excludeGuestId)
      : uniqueResults;
    setRecommendations(filteredResults.slice(0, maxResults));
    setLoading(false);
  };

  const selectGuest = async (guest: Guest | null) => {
    setSelectedGuestState(guest);
    isGuestSelected.current = !!guest;

    if (guest) {
      const displayName = await getGuestDisplayName(guest);
      setInputValue(displayName);

      // Add to invitee state
      setGuestState((prev) =>
        prev.includes(guest) ? prev : [...prev, guest],
      );
    }

    setSelectedGuest?.(guest);
  };

  const handleInputChange = (value: string) => {
    // Prevent changes when disabled is true
    if (disabled) return;

    setInputValue(value);

    // Reset selection flag when user starts typing
    isGuestSelected.current = false;

    // Show dropdown if not already showing
    if (!showRecommendations) {
      // Step 3: Centralize Open Logic (Position First, State Second)
      setDropdownPosition(inputRef);
      setPositionReady(false);
      setShowRecommendations(true);
      onFocusAction?.();
    }

    // Only search if 3 or more characters
    if (value.trim().length >= 3) {
      updateRecommendations(value);
    } else {
      setRecommendations([]);
    }

    // Clear selection only if input is empty
    if (!value.trim()) {
      selectGuest(null);
    }
  };

  const handleInputFocus = () => {
    // Prevent focus action when disabled is true
    if (disabled) return;

    if (!editMode) {
      // Step 3: Centralize Open Logic (Position First, State Second)
      setDropdownPosition(inputRef);
      // Only search if 3 or more characters
      if (inputValue.trim().length >= 3) {
        updateRecommendations(inputValue);
      } else {
        setRecommendations([]);
      }
      setPositionReady(false);
      setShowRecommendations(true);
      onFocusAction?.();
    }
  };

  const handleInputBlur = async () => {
    // Prevent blur action when disabled is true
    if (disabled) return;

    if (isMouseDownOnDropdown.current) {
      isMouseDownOnDropdown.current = false;
      return;
    }

    // If invitee was explicitly selected, keep it
    if (isGuestSelected.current && selectedGuest) {
      onBlurAction?.(selectedGuest);
      setShowRecommendations(false);
      setPositionReady(false);
      return;
    }

    // Use the already resolved names
    const exactMatch = recommendationsWithNames.find(
      ({ displayName }) =>
        displayName.toLowerCase() === inputValue.toLowerCase(),
    );

    if (exactMatch) {
      await selectGuest(exactMatch.guest);
      onBlurAction?.(exactMatch.guest);
    } else if (required && inputValue.trim() && recommendations.length > 0) {
      // Auto-select first result for required fields
      const firstGuest = recommendations[0];
      await selectGuest(firstGuest);
      onBlurAction?.(firstGuest);
    } else {
      await selectGuest(null);
      onBlurAction?.(null);
    }

    setShowRecommendations(false);
    setPositionReady(false);
  };

  const handleGuestSelect = async (guest: Guest) => {
    // Prevent selection when disabled is true
    if (disabled) return;

    await selectGuest(guest);
    setShowRecommendations(false);
    setPositionReady(false);
    onBlurAction?.(guest);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent keyboard interactions when disabled is true
    if (disabled) return;

    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`flex flex-col ${width}`}>
      {/* Tooltip */}
      {labelTooltip}

      {/* Label */}
      {label && (
        <label
          ref={labelRef}
          onMouseEnter={onLabelMouseEnter}
          onMouseLeave={onLabelMouseLeave}
          className={`${labelMargin} w-max font-secondary ${labelFontSize} ${
            hoverMessage && !disabled ? "cursor-help" : ""
          } ${disabled ? "opacity-50" : ""}`}
        >
          {label}
        </label>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        value={inputValue}
        placeholder={placeholder}
        disabled={isLoadingDefaultValue || disabled}
        readOnly={disabled}
        autoComplete="off"
        onMouseEnter={() => !disabled && setDropdownPosition(inputRef)}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        className={`${padding} w-full focus:outline-hidden ${surface} ${rounded} ${height} ${fontSize} ${border} ${
          isLoadingDefaultValue || disabled
            ? "opacity-50 cursor-not-allowed"
            : ""
        } outline-quaternary`}
      />

      {/* Dropdown */}
      {!editMode && !disabled && showRecommendations && positionReady && (
        <div className="fixed z-20 inset-0 h-screen w-screen">
          <ul
            className="fixed w-max p-2 z-20 mt-1 surface-primary border rounded-lg overflow-y-auto outline-quaternary"
            style={{
              ...(dropdownPosition.showAbove
                ? {
                    bottom: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }
                : {
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }),
              // Right-edge clamp: cap at the space left of the viewport edge
              // (floored at 4rem so a near-edge anchor can't collapse the list
              // to zero), keep the 300px floor only while it fits. Off-edge the
              // max never binds, so wide rows still grow like `w-max` did.
              minWidth: `min(18.75rem, max(4rem, calc(100vw - ${dropdownPosition.left}px - 16px)))`,
              maxWidth: `max(4rem, calc(100vw - ${dropdownPosition.left}px - 16px))`,
              maxHeight: `${maxHeight}px`,
            }}
            onMouseDown={() => {
              isMouseDownOnDropdown.current = true;
            }}
          >
            {loading ? (
              <LoadingSpinner />
            ) : recommendationsWithNames.length > 0 ? (
              recommendationsWithNames.map(
                ({ guest, displayName }, index) => (
                  <li
                    key={guest.guestId || index}
                    className="py-1 truncate cursor-pointer hover:surface-tertiary body-3-regular px-2"
                    onClick={() => handleGuestSelect(guest)}
                  >
                    {displayName}
                  </li>
                ),
              )
            ) : (
              <li className="py-8 text-center font-quaternary body-3-regular">
                {inputValue.trim().length < 3
                  ? t("minCharsRequired")
                  : t("noFieldsFound")}
              </li>
            )}
          </ul>
          <div
            className="fixed inset-0 h-screen w-screen"
            onClick={(e) => {
              if (!isFilter) e.stopPropagation();
              setShowRecommendations(false);
              setPositionReady(false);
            }}
          />
        </div>
      )}
    </div>
  );
};
